import { z } from 'zod';
import {
  DependencyTypeSchema,
  type Predecessor,
  type ScheduleItem,
  type ShiftReason,
} from '../../domain/entities/schedule.ts';
import { expectUnchanged, updateWrite, type BatchWrite } from '../../ports/batch.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import { ConcurrentModificationError, NotFoundError } from '../../ports/repository.ts';
import type { Job } from '../../domain/entities/job.ts';
import { endDateFor, nextWorkingDay } from '../../domain/work-calendar.ts';
import type { WorkCalendar } from '../../domain/work-calendar.ts';
import { assertAcyclic, cascadeSchedule, type ScheduleShift } from './dependencies.ts';
import { requiresReason, toShiftRecord, withShift } from './shifts.ts';
import {
  applyItemPatch,
  listScheduleItems,
  parseScheduleItemInput,
  resolveSpan,
  UpdateScheduleItemInputSchema,
  type UpdateScheduleItemInput,
} from './save-schedule-item.ts';
import { loadJobCalendar } from './workday-exceptions.ts';

export interface SchedulePlanContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
  /**
   * What kind of machine a unit is, when saving a schedule as a template
   * (SCRUM-210).
   *
   * Supplied by the caller, which holds the register, rather than read
   * here — this module takes everything it needs and does no fetching of
   * its own, which is what keeps `expandTemplate` pure and showable
   * before anything is written. Optional: a caller with no register in
   * hand saves a template that carries no plant, which is the honest
   * result rather than a guess.
   */
  readonly equipmentTypeOf?: (equipmentId: string) => string | undefined;
}

export const PredecessorInputSchema = z.object({
  itemId: z.string().min(1),
  type: DependencyTypeSchema,
  /** Negative is legitimate: it expresses an overlap. */
  lagDays: z.number().int().min(-60).max(365).default(0),
});
export type PredecessorInput = z.input<typeof PredecessorInputSchema>;

/**
 * A published date was moved without saying why.
 *
 * Separate from a validation error because it is not a malformed request: the
 * move is fine, it just needs a reason attached, and the caller's response is
 * to ask for one rather than to correct a field.
 */
export class ShiftReasonRequiredError extends Error {
  readonly itemTitle: string;

  constructor(itemTitle: string) {
    super(`Moving "${itemTitle}" needs a reason, because subs or the owner have these dates`);
    this.name = 'ShiftReasonRequiredError';
    this.itemTitle = itemTitle;
  }
}

export class PredecessorValidationError extends Error {
  readonly fieldErrors: Record<string, string>;

  constructor(fieldErrors: Record<string, string>) {
    const summary = Object.entries(fieldErrors)
      .map(([field, message]) => `${field}: ${message}`)
      .join('; ');
    super(`Those predecessors need attention — ${summary}`);
    this.name = 'PredecessorValidationError';
    this.fieldErrors = fieldErrors;
  }
}

/**
 * How many times a plan is recomputed when someone else writes first.
 *
 * A cascade is deterministic and derives entirely from stored state, so
 * recomputing from the current schedule is always safe and usually settles
 * immediately. Bounded, because an unbounded retry under sustained
 * contention is a way to hide a problem rather than solve one.
 */
const MAX_PLAN_ATTEMPTS = 3;

/**
 * Run a plan, recomputing it if the schedule moved underneath.
 *
 * Read-compute-write is only safe if the write can tell that the state it was
 * computed from is still current. The guarded writes provide that; this
 * provides the other half, which is doing something sensible about it.
 */
export async function withRetry<T>(plan: () => Promise<T>): Promise<T> {
  for (let attempt = 1; attempt < MAX_PLAN_ATTEMPTS; attempt += 1) {
    try {
      return await plan();
    } catch (cause) {
      if (!(cause instanceof ConcurrentModificationError)) throw cause;
      // Someone else wrote first. Recompute from what the schedule says now.
    }
  }

  // The last attempt is not caught: if it still conflicts, the caller needs
  // to hear about it rather than see a success that did nothing.
  return plan();
}

export interface PlanResult {
  readonly item: ScheduleItem;
  /** Every date the change moved, including the item itself. */
  readonly shifts: readonly ScheduleShift[];
}

function parseOrThrow(input: readonly PredecessorInput[]): Predecessor[] {
  const parsed = z.array(PredecessorInputSchema).safeParse(input);
  if (!parsed.success) {
    const fieldErrors: Record<string, string> = {};
    for (const issue of parsed.error.issues) {
      fieldErrors[issue.path.join('.') || 'form'] ??= issue.message;
    }
    throw new PredecessorValidationError(fieldErrors);
  }
  return parsed.data;
}

/**
 * What the caller is doing, so each movement can be recorded honestly.
 *
 * Distinct from `ShiftAttribution` in `shifts.ts`, which is the finished
 * record: this is what a caller supplies, and the per-item `cascade` flag is
 * derived from it rather than passed in.
 */
export interface ShiftIntent {
  readonly reason?: ShiftReason;
  readonly note?: string;
  /** The item the person acted on, if any. Its own move is not a cascade. */
  readonly directItemId?: string;
}

function itemWrites(
  context: SchedulePlanContext,
  before: readonly ScheduleItem[],
  after: readonly ScheduleItem[],
  now: string,
  attribution: ShiftIntent = {},
  /**
   * Fields to write on the direct item alongside its dates.
   *
   * This is how editing an item's title and its dates stays one commit. The
   * alternative — a detail write followed by a plan write — is two chances to
   * half-save a change, and the maintainer's standing instruction is that a
   * schedule change lands whole or not at all.
   */
  details?: Partial<ScheduleItem>,
): BatchWrite[] {
  const previous = new Map(before.map((item) => [item.id, item]));
  const scope = { companyId: context.companyId, jobId: context.jobId };
  const writes: BatchWrite[] = [];

  for (const item of after) {
    const existing = previous.get(item.id)!;
    const datesMoved = item.startDate !== existing.startDate || item.endDate !== existing.endDate;
    const linksChanged =
      JSON.stringify(item.predecessors) !== JSON.stringify(existing.predecessors);
    const carriesDetails = details !== undefined && item.id === attribution.directItemId;
    if (!datesMoved && !linksChanged && !carriesDetails) continue;

    // Recorded only when dates actually moved: adding a link that changes
    // nothing is not a movement, and logging it would bury the ones that are.
    const shifts = datesMoved
      ? withShift(
          existing,
          toShiftRecord(
            {
              itemId: item.id,
              fromStart: existing.startDate,
              toStart: item.startDate,
              fromEnd: existing.endDate,
              toEnd: item.endDate,
            },
            {
              at: now,
              byUid: context.uid,
              // Everything except the item the person acted on moved because
              // something else did.
              cascade: item.id !== attribution.directItemId,
              reason: attribution.reason,
              note: attribution.note,
            },
          ),
        )
      : existing.shifts;

    writes.push(
      updateWrite<ScheduleItem>(
        scope,
        'scheduleItems',
        item.id,
        {
          // Details first, so a date in the patch can never win over the
          // span the calendar resolved.
          ...(carriesDetails ? details : {}),
          startDate: item.startDate,
          endDate: item.endDate,
          predecessors: item.predecessors,
          shifts,
          updatedAt: now,
          updatedBy: context.uid,
        },
        // Guarded against the version this plan was computed from. Two people
        // dragging bars at the same moment would otherwise both compute from
        // the same snapshot, and the second commit would quietly discard the
        // first person's cascade — no error, just wrong dates.
        expectUnchanged(existing),
      ),
    );
  }

  return writes;
}

/**
 * Set an item's predecessors, then settle the whole schedule.
 *
 * The cascade and the link itself go in **one batch**. A link saved without
 * the moves it implies leaves a schedule whose own dates contradict its
 * dependencies, and whoever opened it next would have no way to tell that
 * from a schedule someone meant.
 *
 * A cycle is refused before anything is written, which is why the check runs
 * against the proposed schedule rather than the stored one.
 */
export async function setPredecessors(
  context: SchedulePlanContext,
  itemId: string,
  predecessors: readonly PredecessorInput[],
): Promise<PlanResult> {
  const parsed = parseOrThrow(predecessors);

  if (parsed.some((predecessor) => predecessor.itemId === itemId)) {
    throw new PredecessorValidationError({ form: 'An item cannot depend on itself' });
  }
  const seen = new Set<string>();
  for (const predecessor of parsed) {
    if (seen.has(predecessor.itemId)) {
      throw new PredecessorValidationError({
        form: 'That item is listed as a predecessor more than once',
      });
    }
    seen.add(predecessor.itemId);
  }

  return withRetry(async () => {
    const items = await listScheduleItems(context);
    const target = items.find((item) => item.id === itemId);
    if (!target) throw new NotFoundError('scheduleItems', itemId);

    const known = new Set(items.map((item) => item.id));
    for (const predecessor of parsed) {
      if (!known.has(predecessor.itemId)) {
        throw new PredecessorValidationError({
          form: 'One of those predecessors is not on this schedule',
        });
      }
    }

    const proposed = items.map((item) =>
      item.id === itemId ? { ...item, predecessors: parsed } : item,
    );

    // Before any write: a loop makes the schedule uncomputable, and refusing
    // it here is what keeps every later cascade terminating. Re-checked on
    // each attempt, because someone else may have added the other half of a
    // loop in the meantime.
    assertAcyclic(proposed);

    const calendar = await loadJobCalendar(context);
    const settled = cascadeSchedule(calendar, proposed);
    const now = context.clock.now().toISOString();

    await context.data.commit(
      itemWrites(context, items, settled.items, now, { directItemId: itemId }),
    );

    return {
      item: settled.items.find((item) => item.id === itemId)!,
      shifts: settled.shifts,
    };
  });
}

/**
 * Edit an item — anything about it — and settle the schedule, in one batch.
 *
 * This is what the item form saves through, and it exists because
 * `updateScheduleItem` is not safe for dates. That use case writes one
 * document: it does not cascade, so changing a start date there leaves every
 * successor behind, and it does not ask `requiresReason`, so a published date
 * could be moved by typing with no explanation — while the very same move by
 * dragging the bar is refused. Two paths to one change, disagreeing about the
 * rules, is how a schedule ends up contradicting itself.
 *
 * So the details and the dates and the cascade all go in one commit. The
 * cost is that `updateScheduleItem` stays the narrow path for changes that
 * cannot move a date — a completion tick, a visibility change — and this is
 * the path for everything else.
 */
export async function editScheduleItem(
  context: SchedulePlanContext,
  itemId: string,
  input: UpdateScheduleItemInput,
  attribution: ShiftIntent = {},
): Promise<PlanResult> {
  const parsed = parseScheduleItemInput(UpdateScheduleItemInputSchema, input);

  return withRetry(async () => {
    const items = await listScheduleItems(context);
    const target = items.find((item) => item.id === itemId);
    if (!target) throw new NotFoundError('scheduleItems', itemId);

    const calendar = await loadJobCalendar(context);
    const touchesDates =
      parsed.startDate !== undefined ||
      parsed.endDate !== undefined ||
      parsed.workDays !== undefined;

    const span = touchesDates
      ? resolveSpan(calendar, {
          startDate: parsed.startDate ?? target.startDate,
          endDate: parsed.endDate,
          workDays: parsed.endDate === undefined ? (parsed.workDays ?? target.workDays) : undefined,
        })
      : { startDate: target.startDate, workDays: target.workDays, endDate: target.endDate };

    const now = context.clock.now().toISOString();
    // Built through the shared patch function, so this and
    // `updateScheduleItem` cannot disagree about what an edit means.
    const edited = applyItemPatch(target, parsed, span, { at: now, uid: context.uid });

    const datesMoved = span.startDate !== target.startDate || span.endDate !== target.endDate;
    if (datesMoved && !attribution.reason) {
      const job = await context.data
        .collection<Job>({ companyId: context.companyId }, 'jobs')
        .get(context.jobId);
      if (job && requiresReason(target, job)) {
        throw new ShiftReasonRequiredError(target.title);
      }
    }

    const proposed = items.map((item) => (item.id === itemId ? edited : item));
    const settled = cascadeSchedule(calendar, proposed);

    // The detail fields only. Dates, predecessors and shifts are the plan's
    // to write, and passing them twice would let a stale value through.
    const {
      id: _id,
      startDate: _start,
      endDate: _end,
      workDays: _days,
      predecessors: _links,
      shifts: _shifts,
      createdAt: _createdAt,
      createdBy: _createdBy,
      ...details
    } = edited;

    await context.data.commit(
      itemWrites(
        context,
        items,
        settled.items,
        now,
        { ...attribution, directItemId: itemId },
        { ...details, workDays: settled.items.find((item) => item.id === itemId)!.workDays },
      ),
    );

    const moved = settled.items.find((item) => item.id === itemId)!;
    const ownShift: ScheduleShift[] =
      moved.startDate === target.startDate && moved.endDate === target.endDate
        ? []
        : [
            {
              itemId,
              fromStart: target.startDate,
              toStart: moved.startDate,
              fromEnd: target.endDate,
              toEnd: moved.endDate,
            },
          ];

    return {
      item: moved,
      shifts: [...ownShift, ...settled.shifts.filter((shift) => shift.itemId !== itemId)],
    };
  });
}

/**
 * Move or resize an item and settle its successors, in one batch.
 *
 * This is the path a Gantt drag takes. It is `editScheduleItem` with nothing
 * but dates, and delegates rather than repeating it: the cascade, the
 * expectation guards and the published-dates reason check all have to behave
 * identically whether a date was dragged or typed, and two copies of that
 * would eventually disagree.
 *
 * Whatever a drag produces is resolved to a date through `resolveSpan`, the
 * same function the form uses, so a drag can never land work on a day nobody
 * works.
 */
export async function moveScheduleItem(
  context: SchedulePlanContext,
  itemId: string,
  dates: Pick<UpdateScheduleItemInput, 'startDate' | 'endDate' | 'workDays'>,
  attribution: ShiftIntent = {},
): Promise<PlanResult> {
  return editScheduleItem(context, itemId, dates, attribution);
}

/**
 * Bring every item's own dates back in line with the calendar.
 *
 * A cascade only ever recomputes a *successor's* start from its
 * predecessors. When the calendar itself changes — a holiday added, a
 * Saturday opened — an item's stored end date is derived from a working week
 * that no longer applies, and nothing downstream would notice. So each span
 * is recomputed from its own start and duration first: a start that landed on
 * a newly closed day rolls forward, and the end follows.
 */
function normaliseSpans(
  calendar: WorkCalendar,
  items: readonly ScheduleItem[],
): { items: ScheduleItem[]; shifts: ScheduleShift[] } {
  const shifts: ScheduleShift[] = [];
  const normalised = items.map((item) => {
    const startDate = nextWorkingDay(calendar, item.startDate);
    const endDate = endDateFor(calendar, startDate, item.workDays);
    if (startDate === item.startDate && endDate === item.endDate) return item;

    shifts.push({
      itemId: item.id,
      fromStart: item.startDate,
      toStart: startDate,
      fromEnd: item.endDate,
      toEnd: endDate,
    });
    return { ...item, startDate, endDate };
  });
  return { items: normalised, shifts };
}

/**
 * Re-settle a schedule whose calendar changed, in one batch.
 *
 * Adding or removing a workday exception moves every date computed over it,
 * so this is what the exceptions page calls after a change. Reports what
 * moved, for the shift history.
 */
export async function resettleSchedule(
  context: SchedulePlanContext,
): Promise<readonly ScheduleShift[]> {
  return withRetry(async () => {
    const items = await listScheduleItems(context);
    if (items.length === 0) return [];

    assertAcyclic(items);
    const calendar = await loadJobCalendar(context);

    const normalised = normaliseSpans(calendar, items);
    const settled = cascadeSchedule(calendar, normalised.items);

    // One shift per item: a cascaded move supersedes the recomputation that
    // preceded it, so the history reads as "this moved from here to there"
    // rather than listing two hops of the same change.
    const byItem = new Map<string, ScheduleShift>();
    for (const shift of normalised.shifts) byItem.set(shift.itemId, shift);
    for (const shift of settled.shifts) {
      const earlier = byItem.get(shift.itemId);
      byItem.set(
        shift.itemId,
        earlier ? { ...shift, fromStart: earlier.fromStart, fromEnd: earlier.fromEnd } : shift,
      );
    }

    const shifts = [...byItem.values()];
    if (shifts.length === 0) return [];

    const now = context.clock.now().toISOString();
    await context.data.commit(
      // No direct item: nobody moved any of these, the calendar underneath
      // them did. The spec names this reason explicitly.
      itemWrites(context, items, settled.items, now, { reason: 'workdayException' }),
    );
    return shifts;
  });
}
